import { assertIsNotUndefined } from "../utils/assertOfType";
import { EntityRelationship } from "./entityGraphTypes";

export class EntityGraph<N extends object, R extends string> {
  /**
   * Map of nodes by hash provided by hashFn
   */
  private readonly _nodes: Map<string, WeakRef<N>> = new Map();
  private readonly _edges: Map<string, Map<string, R>> = new Map();
  private readonly _buckets: Map<string, N[]> = new Map();
  private _currentBucket: string | undefined = undefined;

  get nodes(): N[] {
    return Array.from(this._nodes.values())
      .map((ref) => ref?.deref())
      .filter(notUndefinedOrNull);
  }

  constructor(private readonly hashFn: (entity: N) => string) {}

  startBucket(bucket: string): void {
    if (this._currentBucket) {
      console.warn(`Bucket ${this._currentBucket} was not ended before starting bucket ${bucket}`);
    }
    if (this._buckets.has(bucket)) {
      const nodes = this._buckets.get(bucket) ?? [];
      for (const node of nodes) {
        this.delete(node);
      }
    }
    this._currentBucket = bucket;
    this._buckets.set(bucket, []);
  }

  endBucket(): void {
    this._currentBucket = undefined;
  }

  /** Drop every node, edge, and bucket so the graph can be rebuilt from scratch. */
  clear(): void {
    this._nodes.clear();
    this._edges.clear();
    this._buckets.clear();
    this._currentBucket = undefined;
  }

  add(entity: N): N {
    const hash = this.hashFn(entity);
    if (this.has(entity)) {
      const existingNode = this.nodeByHash(hash);
      assertIsNotUndefined(existingNode, "Node deletion race condition detected");
      return existingNode;
    }
    const newRef = new WeakRef(entity);
    this._nodes.set(hash, newRef);
    if (this._currentBucket) {
      this._buckets.get(this._currentBucket)?.push(entity);
    }
    return entity;
  }

  has(entity: N): boolean {
    const hash = this.hashFn(entity);
    return this._nodes.get(hash)?.deref() !== undefined;
  }

  delete(entity: N): void {
    const hash = this.hashFn(entity);
    const relationships = this._edges.get(hash);
    if (relationships) {
      // Edge keys are `${type}.${neighborHash}`, so recover each neighbor and drop its
      // reverse edge (`${type}.${hash}`) rather than treating the key as a bare node hash.
      for (const [relHash, type] of relationships) {
        const neighborHash = relHash.slice(type.length + 1);
        this._edges.get(neighborHash)?.delete(`${type}.${hash}`);
      }
    }
    this._nodes.delete(hash);
    this._edges.delete(hash);
  }

  relate(a: N, type: R, b: N, bidirectional: boolean = true): string {
    const aHash = this.hashFn(a);
    const bHash = this.hashFn(b);
    const rHash = `${type}.${bHash}`;
    if (this._edges.get(aHash)?.has(rHash)) {
      return rHash;
    }
    if (!this._edges.has(aHash)) {
      this._edges.set(aHash, new Map());
    }
    this._edges.get(aHash)?.set(rHash, type);
    if (bidirectional) {
      this.relate(b, type, a, false);
    }
    return rHash;
  }

  node(entity: N): N {
    const hash = this.hashFn(entity);
    let node = this.nodeByHash(hash);
    if (!node) {
      node = this.add(entity);
    }
    return node;
  }

  relationships(
    entity: N,
    predicate?: (relationship: EntityRelationship<string, R>) => boolean,
  ): EntityRelationship<string, R>[] {
    const hash = this.hashFn(entity);
    return Array.from(this._edges.get(hash)?.entries() ?? []).reduce<EntityRelationship<string, R>[]>(
      (acc, [relHash, type]) => {
        const [, ...rest] = relHash.split(".");
        const neighborHash = rest.join(".");
        const relationship = new EntityRelationship(type, hash, neighborHash);
        if (predicate && !predicate(relationship)) {
          return acc;
        }
        acc.push(relationship);
        return acc;
      },
      [] as EntityRelationship<string, R>[],
    );
  }

  nodeByHash(hash: string): N | undefined {
    return this._nodes.get(hash)?.deref();
  }

  breadthFirstSearch(root: N, depth: number = 3): N[] {
    if (depth < 0) {
      return [];
    }

    const notRoot = notNode(root);

    const results = new Set<N>();
    const visited = new WeakMap<N, boolean>();
    const queue: Array<[N, number]> = [];

    // Start with the root node at depth 0
    visited.set(root, true);
    queue.push([root, 0]);

    while (queue.length > 0) {
      const item = queue.shift();
      if (!item) {
        continue;
      }
      const [node, currentDepth] = item;

      // Stop if we've reached the maximum depth
      if (currentDepth >= depth) {
        continue;
      }

      // Get neighbors of the current node
      const neighbors = this.graphNeighbors(node);

      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          visited.set(neighbor, true);
          if (notRoot(neighbor)) {
            results.add(neighbor);
          }
          queue.push([neighbor, currentDepth + 1]);
        }
      }
    }

    return Array.from(results);
  }

  private graphNeighbors(node: N): N[] {
    return this.relationships(node)
      .map((r) => this.nodeByHash(r.b))
      .filter(notUndefinedOrNull);
  }
}

export function notNode<N>(node: N): (n: N) => boolean {
  return (n: N) => n !== node;
}

export function notUndefinedOrNull<T>(item: T | undefined | null): item is T {
  return item !== undefined && item !== null;
}
